<script lang="ts">
import DocumentCardImage, {
  DocumentCardImageType,
} from '@/V2/components/documents/DocumentCardImage.vue';
import ExpandableContainerItem from '@/V2/components/expandable-container/ExpandableContainerItem.vue';
import StatusBadge from '@/V2/components/status/StatusBadge.vue';
import { Statuses } from '@/V2/components/status/utils';
import { computed, ref } from 'vue';
import { getFileTypeText } from '@/utils/fileType';
import { getDocumentTypeText } from '@/utils/documentType';
import {
  mapRunToStatus,
  mapRunToLabel,
} from '@/V2/documents/document-view/document-review-agent/utils';
import {
  iBadgeWithCount,
  iBadgeWithCountTypes,
  iSpinner,
} from '@inscribe/signal';
import TrustScoreDisplay, {
  TrustScoreDisplaySizes,
} from '@/V2/documents/TrustScoreDisplay';
import { SEVERITY_LEVEL_BY_FRAUD_SIGNAL } from '@/constants/signalNames';
import { VERIFICATION_RESULT_SORT_ORDER } from '@/constants/analyst-constants';
import { getSignalName } from '@/utils/signalNames';
import { mapGetters } from 'vuex';
import * as documentStates from '@/constants/documentStates';

export const DocumentCardSizes = {
  SMALL: 'small',
  MEDIUM: 'medium',
};

export default {
  name: 'DocumentCard',
  components: {
    DocumentCardImage,
    ExpandableContainerItem,
    StatusBadge,
    iBadgeWithCount,
    TrustScoreDisplay,
    iSpinner,
  },
  props: {
    document: {
      type: Object,
      required: false,
    },
    size: {
      type: String,
      default: DocumentCardSizes.MEDIUM,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    limitedWidth: {
      type: Number,
      default: null,
    },
    rightIcon: {
      type: String,
      default: null,
    },
    showTags: {
      type: Boolean,
      default: true,
    },
    showSignals: {
      type: Boolean,
      default: true,
    },
    showChecks: {
      type: Boolean,
      default: true,
    },
    returnTo: {
      type: String,
      default: null,
    },
  },

  setup(props) {
    const getDocumentBadgeText = computed(() => {
      let riskLevel =
        props.document?.risk_level ||
        props.document?.risk_assessment?.risk_level;
      const object = riskLevel
        ? {
            status: 'FINISHED',
            output: {
              risk_level: riskLevel,
            },
          }
        : props.document?.document_review_agent_v1;
      return mapRunToLabel(object);
    });

    const getDocumentBadgeState = computed(() => {
      let riskLevel =
        props.document?.risk_level ||
        props.document?.risk_assessment?.risk_level;
      const object = riskLevel
        ? {
            status: 'FINISHED',
            output: {
              risk_level: riskLevel,
            },
          }
        : props.document?.document_review_agent_v1;
      return mapRunToStatus(object);
    });

    const getDocumentImageType = computed(() => {
      return props.loading
        ? DocumentCardImageType.LOADING
        : props.document.review_status === 'ACCEPTED'
          ? DocumentCardImageType.ACCEPTED
          : props.document.review_status === 'REJECTED'
            ? DocumentCardImageType.REJECTED
            : DocumentCardImageType.DEFAULT;
    });

    const getDocumentErrorLabel = computed(() => {
      let returnLabel = null;
      switch (props.document?.state) {
        case documentStates.CORRUPT_DATA_DETECTED:
          returnLabel = 'Corrupt Data';
          break;
        case documentStates.PASSWORD_PROTECTED:
          returnLabel = 'Password Protected';
          break;
        case documentStates.ERROR_PROCESSING:
          returnLabel = 'Error Processing';
          break;
        case documentStates.EXCEEDS_MAX_PAGE_COUNT:
          returnLabel = 'Exceeds Max Page Count';
          break;
        case documentStates.VIRUS_DETECTED:
          returnLabel = 'Virus Detected';
          break;
        case documentStates.ERROR_UPLOADING:
          returnLabel = 'Error Uploading';
          break;
        case documentStates.IN_HUMAN_REVIEW:
          returnLabel = 'In Human Review';
          break;
      }
      return returnLabel;
    });

    const documentSubtitle = computed(() => {
      let documentType = getDocumentTypeText(props.document?.document_type);
      return `${getDocumentErrorLabel.value ?? documentType} • ${getFileTypeText(props.document?.file_type)}`;
    });

    return {
      DocumentCardSizes,
      Statuses,
      getDocumentBadgeText,
      getDocumentBadgeState,
      getDocumentImageType,
      documentSubtitle,
      iBadgeWithCountTypes,
      contentResizeObserver: null,
      showCollapsedBadge: ref(false),
      TrustScoreDisplaySizes,
    };
  },

  computed: {
    ...mapGetters(['canViewAIFraudAnalyst', 'getDocumentBeingDeleted']),
    showTrustScore() {
      return !this.canViewAIFraudAnalyst;
    },
    isProcessing() {
      return [documentStates.PROCESSING, documentStates.UPLOADING].includes(
        this.document?.state,
      );
    },
    isDocumentBeingDeleted() {
      return this.getDocumentBeingDeleted(this.document?.id);
    },
    hasRightAction() {
      return !!this.$slots['right-action'];
    },
    cardIsClickable() {
      return !!this.document?.id && !this.cardIsGrayedOut;
    },
    cardIsGrayedOut() {
      return [
        documentStates.PASSWORD_PROTECTED,
        documentStates.ERROR_PROCESSING,
        documentStates.VIRUS_DETECTED,
        documentStates.ERROR_UPLOADING,
        documentStates.CORRUPT_DATA_DETECTED,
      ].includes(this.document?.state);
    },
    documentTagsList() {
      return this.document?.tags?.map((tag) => tag.value);
    },
    documentSignalList() {
      const signalObject = this.document?.fraud_results || {};
      return Object.keys(signalObject)
        .filter((signalKey) => {
          return signalObject[signalKey].failed === true;
        })
        .map((signalKey) => {
          return {
            signalKey,
            severityLevel: SEVERITY_LEVEL_BY_FRAUD_SIGNAL[signalKey],
            name: getSignalName(signalKey),
          };
        })
        .sort((signal1, signal2) => {
          return VERIFICATION_RESULT_SORT_ORDER[signal1.severityLevel] >
            VERIFICATION_RESULT_SORT_ORDER[signal2.severityLevel]
            ? 1
            : -1;
        })
        .map((signalObject) => signalObject.name);
    },
    documentCheckList() {
      return this.document?.checks
        ?.filter((check) => check.passed === false) // small solid field
        .map((check, idx) =>
          check.name && check.name !== '' ? check.name : `Check ${idx}`,
        );
    },
    shouldShowRiskLevel() {
      return (
        this.document?.risk_level ||
        this.document?.document_review_agent_v1 ||
        this.document?.risk_assessment?.risk_level
      );
    },
    hasValidDocumentName() {
      return this.document?.name && this.document?.name !== '';
    },
    hasTrustScore() {
      return (
        this.document?.trust_score !== 'undefined' &&
        this.document?.trust_score !== null
      );
    },
    subtitle() {
      return this.isProcessing
        ? 'Processing...'
        : this.isDocumentBeingDeleted
          ? 'Deleting...'
          : this.documentSubtitle;
    },
    documentLink() {
      return this.document
        ? `/customer/${this.document?.customer_id}/document/${
            this.document?.id
          }/signals${this.returnTo ? `?return_to=${this.returnTo}` : ''}`
        : '/';
    },
  },
  methods: {
    setupContentResizeObserver() {
      const itemElement = this.$refs.documentCardContainerItem;
      if (!itemElement) return;
      this.contentResizeObserver = new ResizeObserver(() => {
        const cardWidth = itemElement.$el?.getBoundingClientRect().width || 0;
        this.showCollapsedBadge = cardWidth < 1000;
      });
      this.contentResizeObserver.observe(itemElement.$el);
    },
    onItemClick(evt: Event) {
      if (this.cardIsClickable) {
        evt.stopPropagation();
        this.$emit('on-document-click', this.document);
      }
    },
    setTextLimitedWidth() {
      if (this.limitedWidth && this.document.risk_level) {
        const itemElement = this.$refs.documentCardContainerItem;
        if (itemElement) {
          const titleElement = itemElement.$el?.querySelector(
            '.text-container-title',
          );
          const subtitleElement = itemElement.$el?.querySelector(
            '.text-container-subtitle',
          );
          if (titleElement) {
            titleElement.style.maxWidth = this.limitedWidth + 'px';
          }
          if (subtitleElement) {
            subtitleElement.style.maxWidth = this.limitedWidth + 'px';
          }
        }
      }
    },
  },
  mounted() {
    this.setTextLimitedWidth();
    this.setupContentResizeObserver();
  },
  unmounted() {
    this.contentResizeObserver?.disconnect();
  },
};
</script>

<template>
  <expandable-container-item
    ref="documentCardContainerItem"
    :class="{
      small: size === DocumentCardSizes.SMALL,
      'ellipses-text': limitedWidth && document.risk_level,
      clickable: cardIsClickable,
    }"
    :grayed-out="cardIsGrayedOut"
    class="document-card i-radius-xl i-pl-4 i-pr-8 i-py-6"
    :title="document?.name"
    :subtitle="subtitle"
    :has-right-icon="!hasRightAction"
    :right-icon="(!hasRightAction && rightIcon) || undefined"
    :title-link="cardIsClickable ? documentLink : undefined"
    :is-clickable="cardIsClickable"
    data-testid="document-card"
    @on-item-click="onItemClick"
    @on-right-icon-click="onItemClick"
    @on-title-link-click="$emit('on-title-link-click')"
  >
    <!-- left -->
    <template #left-container>
      <div ref="leftContainer">
        <document-card-image
          :class="{ 'linear-background': loading }"
          :loading="loading"
          :processing="isProcessing || isDocumentBeingDeleted"
          :document="document"
          :type="getDocumentImageType"
          :size="size"
        />
      </div>
    </template>

    <template v-if="loading && !isProcessing && !isDocumentBeingDeleted" #body>
      <div
        class="loading-state accordion-content d-flex flex-column align-items-start i-gap-8"
        data-testid="run-processing"
      >
        <div
          v-if="hasValidDocumentName"
          class="name i-text-button-2"
          data-testid="card-item-title"
        >
          {{ document?.name }}
        </div>
        <div class="lines d-flex flex-column i-gap-4">
          <div
            v-for="(length, index) in [111, 263]"
            :key="index"
            :style="{ width: length + 'px' }"
            class="line linear-background"
          ></div>
        </div>
      </div>
    </template>

    <!-- right -->
    <template #right-container>
      <i-spinner
        v-if="isProcessing || isDocumentBeingDeleted"
        size="small"
        :class="{ 'i-mr-8': hasRightAction }"
      />

      <template v-else>
        <div
          class="d-flex align-items-center i-gap-8"
          ref="rightContainer"
          :class="{ 'i-mr-8': hasRightAction }"
        >
          <status-badge
            v-if="document?.collect_session?.live_capture_mode"
            :outline="true"
            :label="showCollapsedBadge ? '' : 'Live Image Capture'"
            :has-trailing-line="true"
            icon="small-solid-camera-1"
          />

          <!-- tags -->
          <div
            v-if="showTags && documentTagsList?.length"
            data-testid="badge-tags-container"
          >
            <i-badge-with-count
              :type="iBadgeWithCountTypes.NEUTRAL"
              :items="documentTagsList"
              :collapsed="showCollapsedBadge"
              class="document-tags-badge"
              title="Tags"
            />
          </div>

          <!-- checks -->
          <i-badge-with-count
            v-if="showChecks"
            :type="iBadgeWithCountTypes.WARNING"
            icon="small-solid-file-delete-2"
            :items="documentCheckList"
            :collapsed="showCollapsedBadge"
            title="Requirements"
          />

          <!-- show the trust score or risk level -->
          <template v-if="!loading && showTrustScore">
            <!-- signals -->
            <i-badge-with-count
              v-if="showSignals"
              :type="iBadgeWithCountTypes.ERROR"
              icon="small-solid-alert-circle"
              :items="documentSignalList"
              :collapsed="showCollapsedBadge"
              title="Signals"
            />

            <trust-score-display
              v-if="hasTrustScore"
              class="trust-score"
              :score="document.trust_score"
              :is-analyst="getDocumentBadgeState === Statuses.WARNING"
              :size="TrustScoreDisplaySizes.MEDIUM"
            />
          </template>
          <template v-else-if="!loading">
            <status-badge
              v-if="shouldShowRiskLevel"
              :state="getDocumentBadgeState"
              :label="getDocumentBadgeText"
            />
          </template>
        </div>
      </template>

      <slot name="right-action"></slot>
    </template>
  </expandable-container-item>
</template>

<style lang="scss" scoped>
.document-card {
  background-color: var(--base);
  border: 1px solid var(--border-neutral-subtle);
  cursor: default;
  box-sizing: border-box;
  height: 104px;
  width: 100%;

  :deep(a) {
    pointer-events: none;
  }

  &.clickable {
    cursor: pointer;
    :deep(a) {
      pointer-events: auto;
    }
  }

  &.small {
    height: 88px;
  }

  :deep(.text-container-title),
  :deep(.text-container-subtitle) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.ellipses-text {
    :deep(.text-container-title) {
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }
}

.loading-state {
  .line {
    height: 10px;
    border-radius: 100px;
  }

  .name {
    color: var(--text-neutral-bold);
  }
}
</style>
